import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Phone, Building, ArrowLeft, Plus, MessageSquare, Calendar, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClientDetailPage = () => {
  const { clientId } = useParams();
  const { token } = useAuth();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({
    type: 'note',
    subject: '',
    description: '',
    contact_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchData();
  }, [clientId]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [clientRes, allProjectsRes, contactsRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`, { headers }),
        axios.get(`${API}/projects`, { headers }),
        axios.get(`${API}/contacts/client/${clientId}`, { headers })
      ]);
      
      setClient(clientRes.data);
      setProjects(allProjectsRes.data.filter(p => p.client_id === clientId));
      setContacts(contactsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contacts`, {
        client_id: clientId,
        ...contactForm
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowContactModal(false);
      setContactForm({
        type: 'note',
        subject: '',
        description: '',
        contact_date: new Date().toISOString().split('T')[0]
      });
      fetchData();
    } catch (error) {
      console.error('Failed to create contact:', error);
    }
  };

  const getContactIcon = (type) => {
    switch (type) {
      case 'call': return <Phone className="h-4 w-4" />;
      case 'email': return <Mail className="h-4 w-4" />;
      case 'meeting': return <Calendar className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const getStatusClass = (status) => {
    const statusMap = {
      active: 'status-active',
      planning: 'status-planning',
      completed: 'status-completed',
      on_hold: 'status-on-hold'
    };
    return statusMap[status] || 'status-inactive';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-xl text-slate-gray">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="client-detail-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Link to="/dashboard/clients">
              <Button variant="outline" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Clients
              </Button>
            </Link>
            <div>
              <h1 className="text-4xl font-bold text-carbon-black">{client?.name}</h1>
              <p className="text-slate-gray">{client?.company}</p>
            </div>
          </div>
          <Dialog open={showContactModal} onOpenChange={setShowContactModal}>
            <DialogTrigger asChild>
              <Button className="gradient-fearless text-white" data-testid="add-contact-btn">
                <Plus className="mr-2 h-5 w-5" />
                Log Activity
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Log Client Activity</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleContactSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-semibold text-carbon-black mb-2">Type</label>
                  <Select value={contactForm.type} onValueChange={(value) => setContactForm({ ...contactForm, type: value })}>
                    <SelectTrigger data-testid="contact-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="note">Note</SelectItem>
                      <SelectItem value="call">Phone Call</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="meeting">Meeting</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-carbon-black mb-2">Subject</label>
                  <input
                    type="text"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm({ ...contactForm, subject: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                    required
                    data-testid="contact-subject-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-carbon-black mb-2">Description</label>
                  <textarea
                    value={contactForm.description}
                    onChange={(e) => setContactForm({ ...contactForm, description: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                    rows="4"
                    required
                    data-testid="contact-description-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-carbon-black mb-2">Date</label>
                  <input
                    type="date"
                    value={contactForm.contact_date}
                    onChange={(e) => setContactForm({ ...contactForm, contact_date: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border border-cool-gray focus:outline-none focus:ring-2 focus:ring-fearless-blue"
                    data-testid="contact-date-input"
                  />
                </div>
                <Button type="submit" className="w-full gradient-fearless text-white" data-testid="contact-submit-btn">
                  Save Activity
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Client Info Card */}
          <Card className="shadow-lg rounded-xl border-0">
            <CardHeader>
              <CardTitle>Client Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center text-slate-gray">
                <Mail className="h-5 w-5 mr-3 text-fearless-blue" />
                <span>{client?.email}</span>
              </div>
              {client?.phone && (
                <div className="flex items-center text-slate-gray">
                  <Phone className="h-5 w-5 mr-3 text-fearless-blue" />
                  <span>{client?.phone}</span>
                </div>
              )}
              {client?.company && (
                <div className="flex items-center text-slate-gray">
                  <Building className="h-5 w-5 mr-3 text-fearless-blue" />
                  <span>{client?.company}</span>
                </div>
              )}
              <div className="pt-3 border-t border-cool-gray">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  client?.status === 'active' ? 'status-active' : 'status-inactive'
                }`}>
                  {client?.status}
                </span>
              </div>
              {client?.notes && (
                <div className="pt-3 border-t border-cool-gray">
                  <p className="text-sm font-semibold text-carbon-black mb-1">Notes</p>
                  <p className="text-sm text-slate-gray">{client?.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Projects Summary */}
          <Card className="shadow-lg rounded-xl border-0 lg:col-span-2">
            <CardHeader>
              <CardTitle>Associated Projects ({projects.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {projects.length === 0 ? (
                <p className="text-slate-gray text-center py-8">No projects for this client yet</p>
              ) : (
                <div className="space-y-3">
                  {projects.map((project) => (
                    <Link key={project.id} to={`/dashboard/projects/${project.id}`}>
                      <div className="p-4 bg-cloud-white rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-semibold text-carbon-black">{project.title}</h3>
                            <p className="text-sm text-slate-gray mt-1">${project.budget.toLocaleString()}</p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusClass(project.status)}`}>
                            {project.status}
                          </span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Activity Timeline */}
        <Card className="shadow-lg rounded-xl border-0">
          <CardHeader>
            <CardTitle className="flex items-center">
              <MessageSquare className="h-5 w-5 mr-2 text-fearless-blue" />
              Activity Timeline ({contacts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {contacts.length === 0 ? (
              <p className="text-slate-gray text-center py-8">No activity logged yet</p>
            ) : (
              <div className="space-y-4">
                {contacts.map((contact) => (
                  <div key={contact.id} className="flex items-start space-x-4 p-4 bg-cloud-white rounded-lg" data-testid={`contact-${contact.id}`}>
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-fearless-blue">
                      {getContactIcon(contact.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-carbon-black">{contact.subject}</h3>
                        <span className="text-xs text-slate-gray">
                          {new Date(contact.contact_date).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-gray mb-2">{contact.description}</p>
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-200 text-slate-gray">
                        {contact.type}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default ClientDetailPage;